import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { getOnboardingCookie } from 'shared/containers/PurchaseTermsContainer/utils/cookie';
import { selectWasChatOpened, selectChatOnboardingAvailable } from 'shared/selectors/chatOnboarding';
import { selectIsPurchaseTermsEnabled } from 'shared/selectors/featureToggle';
import { selectPurchaseTerms } from 'shared/selectors/purchaseTerms';

import { useVisibility } from './useVisibility';
import { CHAT_ONBOARDING_TEXT } from '../constants';
import { TChatOnboardingType } from '../types';
import { getChatOnboardingCookie, setChatOnboardingCookie } from '../utils';

interface IChatOnboardingResult {
  open: boolean;
  onboardingText: string;
  closeHandler: () => void;
  ref: React.MutableRefObject<HTMLElement | null>;
}

export const useChatOnboarding = (onboardingType: TChatOnboardingType): IChatOnboardingResult => {
  const [open, setOpen] = useState(false);
  const { ref, isVisible } = useVisibility();
  const { config } = useApplicationContext();

  const builderChatWasOpened = useSelector(selectWasChatOpened);
  const isOnboardingAvailable = useSelector(selectChatOnboardingAvailable);
  const isOnboardingShown = getChatOnboardingCookie(onboardingType);
  const onboardingText = config.get<string>(CHAT_ONBOARDING_TEXT[onboardingType]) ?? '';

  const isPurchaseTermsEnabled = useSelector(selectIsPurchaseTermsEnabled);
  const purchaseTerms = useSelector(selectPurchaseTerms);

  const closeHandler = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    const isPurchaseTermsOnboardingShown = getOnboardingCookie('purchase_terms_onboarding_shown');
    const isBlocked = !isPurchaseTermsOnboardingShown && isPurchaseTermsEnabled && purchaseTerms;

    if (isOnboardingAvailable && !isOnboardingShown && !isBlocked) {
      setOpen(isVisible);

      if (isVisible) {
        setChatOnboardingCookie(onboardingType);
      }
    }
  }, [isOnboardingAvailable, isOnboardingShown, isPurchaseTermsEnabled, isVisible, onboardingType, purchaseTerms]);

  useEffect(() => {
    if (onboardingType === 'builder' && open && builderChatWasOpened) {
      closeHandler();
    }
  }, [open, builderChatWasOpened, closeHandler, onboardingType]);

  return {
    open,
    onboardingText,
    closeHandler,
    ref,
  };
};
